// Shared 3D-context setup + entrance/exit constants for the dropdown +
// inline-comment-form family. Both components animate small popovers that
// open with a subtle fade + slide-down + rotateX; they were independently
// open-coding `parent.style.perspective = "800px"` and re-declaring the
// same GSAP from/to vars. Centralising prevents the two surfaces from
// drifting in motion language and reduces three call sites to one helper.
//
// Pure with respect to inputs: `ensurePerspective` mutates the parent's
// inline style and the element's transform-origin, but it does NOT touch
// anything that doesn't already exist on the DOM. The constants are
// frozen so callers can't accidentally mutate the shared from/to vars.

// Perspective applied to the parent of the animated element so the
// rotateX has a real 3D viewing distance. 800px matches the auth-card,
// details-fold, dropdown, and inline-comment-form precedent.
export const POPOVER_PERSPECTIVE = "800px";

// Hidden / pre-entry state: small upward offset + back-tilt so the open
// animation reads as the popover settling down toward the user. Also
// the same state we tween TO on exit, so the close mirrors the open.
//
// NOT `Object.freeze`d — GSAP mutates animation configs in place
// (adds `duration`, `ease`, etc. internally), so a frozen object
// passed to `gsap.to/fromTo/set` throws
// `TypeError: Cannot add property duration, object is not extensible`
// at runtime, silently breaking every interactive Wallaby test that
// indirectly touches the dropdown/inline-comment-form. Callers
// spread these into a fresh object (`{ ...POPOVER_HIDDEN_VARS }`)
// before handing them to GSAP, which protects the shared constant
// from accidental mutation just as effectively without the freeze.
export const POPOVER_HIDDEN_VARS = {
  opacity: 0,
  y: -6,
  rotationX: -18,
};

// Fully-visible state: flat, no offset, full opacity. The duration +
// ease are duplicated here rather than baked into HIDDEN/VISIBLE
// because individual call sites pick their own (dropdown out is faster
// than dropdown in; inline-comment-form clearProps on completion).
export const POPOVER_VISIBLE_VARS = {
  opacity: 1,
  y: 0,
  rotationX: 0,
};

// Set perspective on the parent (if it doesn't already have one — we
// never overwrite a stronger opinion from an enclosing component) and
// transform-origin on the animated element so the rotateX hinges from
// the top edge rather than the centre. Idempotent; safe to call on
// every mount + every animate-in.
export const ensurePerspective = (el: HTMLElement): void => {
  const parent = el.parentElement;
  if (parent && !parent.style.perspective) {
    parent.style.perspective = POPOVER_PERSPECTIVE;
  }
  el.style.transformOrigin = "top center";
};
